import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestHeaders,
  type InternalAxiosRequestConfig
} from 'axios'

const baseUrl = (import.meta.env.VITE_APP_BASE_URL || '').replace(/\/+$/, '')
const CSRF_ENDPOINT = `${baseUrl}/csrf-token`

let cachedToken: string | null = null
let inflight: Promise<string> | null = null

export function clearCsrfToken(): void {
  cachedToken = null
  inflight = null
}

export async function getCsrfToken(force = false): Promise<string> {
  if (force) {
    clearCsrfToken()
  }
  if (cachedToken) {
    return cachedToken
  }
  if (!inflight) {
    inflight = fetchCsrfToken()
      .then(token => {
        cachedToken = token
        return token
      })
      .finally(() => {
        inflight = null
      })
  }
  return inflight
}

async function fetchCsrfToken(): Promise<string> {
  const { data } = await axios.get<{ result?: { csrf_token?: string } }>(CSRF_ENDPOINT, {
    // Omit cookies so CORS may use Access-Control-Allow-Origin: * on the API.
    withCredentials: false,
  })
  const token = data?.result?.csrf_token
  if (!token || typeof token !== 'string') {
    throw new Error('Failed to obtain CSRF token')
  }
  return token
}

type ConfigWithCsrfRetry = InternalAxiosRequestConfig & { __csrfRetried?: boolean }

export function attachCsrfMutatingInterceptor(instance: AxiosInstance): void {
  instance.interceptors.request.use(
    async config => {
      const method = (config.method ?? 'get').toUpperCase()
      if (!['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
        return config
      }
      const csrf = await getCsrfToken()
      if (!config.headers) {
        config.headers = {} as AxiosRequestHeaders
      }
      ;(config.headers as Record<string, string>)['X-CSRF-Token'] = csrf
      return config
    },
    (error: AxiosError) => Promise.reject(error)
  )

  instance.interceptors.response.use(
    response => response,
    async (error: AxiosError) => {
      const cfg = error.config as ConfigWithCsrfRetry | undefined
      if (error.response?.status === 419 && cfg && !cfg.__csrfRetried) {
        cfg.__csrfRetried = true
        clearCsrfToken()
        const fresh = await getCsrfToken(true)
        if (!cfg.headers) {
          cfg.headers = {} as AxiosRequestHeaders
        }
        ;(cfg.headers as Record<string, string>)['X-CSRF-Token'] = fresh
        return instance.request(cfg)
      }
      return Promise.reject(error)
    }
  )
}
